/**
 * Minimal semver comparison for the version gate (web-client.md §7, protocol.md §11). The backend
 * emits plain `major.minor.patch` tokens (VersionManifestService defaults `0.1.0`); we only need a
 * total order, not full semver range logic. Pre-release/build metadata is ignored (compared by the
 * numeric core only), which is sufficient for the min/latest gate. DOM-free, pure.
 */
export interface SemVer {
  major: number;
  minor: number;
  patch: number;
}

/** Parse `major.minor.patch` (extra segments/suffixes ignored). Returns null when unparseable. */
export function parseSemver(input: string): SemVer | null {
  const core = input.trim().split(/[-+]/, 1)[0] ?? '';
  const parts = core.split('.');
  if (parts.length === 0 || parts[0] === '') return null;
  const nums = parts.slice(0, 3).map((p) => Number(p));
  if (nums.some((n) => !Number.isFinite(n) || n < 0)) return null;
  return { major: nums[0] ?? 0, minor: nums[1] ?? 0, patch: nums[2] ?? 0 };
}

/** Total order over two version strings: -1 (a<b), 0 (equal), 1 (a>b). Unparseable sorts as 0.0.0. */
export function compareSemver(a: string, b: string): -1 | 0 | 1 {
  const pa = parseSemver(a) ?? { major: 0, minor: 0, patch: 0 };
  const pb = parseSemver(b) ?? { major: 0, minor: 0, patch: 0 };
  for (const key of ['major', 'minor', 'patch'] as const) {
    if (pa[key] < pb[key]) return -1;
    if (pa[key] > pb[key]) return 1;
  }
  return 0;
}
